import urls from './urls';
import axios, { AxiosResponse } from 'axios';

interface GroupChatMemberParams {
  toString(): string;
}

interface TokenValidationParams {
  xmppId: string;
  token: string;
}

interface GroupChatApiConfig {
  isAnonymous: boolean;
}

interface GroupChatApiOptions {
  baseURL: string;
  headers: GroupChatApiConfig;
}

class GroupChatApi {
  static getGroupsChat(): Promise<AxiosResponse> {
    return axios
      .get(urls.GET_GROUPS_CHAT, {
        baseURL: urls.BASE_URL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static getGroupChatMembers(params: GroupChatMemberParams): Promise<AxiosResponse> {
    const url = urls.GET_PROFILE_SUMMARIES.replace('{ids}', params.toString());
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } }).then(res => res);
  }

  static updateUserStatus(xmppId: string, params: any): Promise<AxiosResponse> {
    const url = urls.UPDATE_USER_STATUS;
    return axios.put(url, params, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } }).then(res => res);
  }

  static getListUsersStatus(params: any, roomJid: string): Promise<AxiosResponse> {
    const url = urls.GET_LIST_USERS_STATUS.replace('{roomJid}', encodeURIComponent(roomJid));
    return axios.post(url, params, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } }).then(res => res);
  }

  static validateToken(token: string, receiverXmppid: string): Promise<AxiosResponse> {
    const url = urls.RHP_POST_VALIDATE_TOKEN;
    const validationParams: TokenValidationParams = {
      xmppId: receiverXmppid.toString(),
      token: token.toString(),
    };
    return axios.post(url, validationParams, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } }).then(res => res);
  }

  static getChatRoomAuthorized(): Promise<AxiosResponse> {
    return axios
      .get(urls.AUTHORIZE_CHAT_ROOM, {
        baseURL: urls.BASE_RHPURL,
        headers: { isAnonymous: false },
      })
      .then(res => res);
  }

  static createUserChatroomBan(params: any, userBanedId: string): Promise<AxiosResponse> {
    const url = urls.CREATE_USER_CHATROOM_BAN.replace('{id}', userBanedId.toString());
    return axios.post(url, params, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } }).then(res => res);
  }

  static getNoteBanChatroom(params: any, userBanedId: string): Promise<AxiosResponse> {
    const url = urls.GET_NOTE_CHATROOM.replace('{id}', userBanedId.toString());
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } }).then(res => res);
  }

  static postNoteBanChatroom(params: any, userBanedId: string): Promise<AxiosResponse> {
    const url = urls.POST_NOTE_CHATROOM.replace('{id}', userBanedId.toString());
    return axios.post(url, params, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } }).then(res => res);
  }

  static getFreeHoursChatRoom(): Promise<AxiosResponse> {
    const url = urls.GET_FREE_HOURS_CHAT_ROOM;
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } }).then(res => res);
  }

  static postJoinGroupSuccess(roomJid: string, shareToken?: string): Promise<AxiosResponse> {
    const url = urls.POST_JOIN_GROUP_SUCCESS.replace('{roomJid}', encodeURIComponent(roomJid));
    return axios.post(url, shareToken ? { shareToken } : null, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } }).then(res => res);
  }

  static postLeaveGroupSuccess(roomJid: string): Promise<AxiosResponse> {
    const url = urls.POST_LEAVE_GROUP_SUCCESS.replace('{roomJid}', encodeURIComponent(roomJid));
    return axios.post(url, null, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } }).then(res => res);
  }

  static postSignalCheckInRoom(params: any): Promise<AxiosResponse> {
    const url = urls.SEND_NOTIFICATION;
    return axios.post(url, params, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } }).then(res => res);
  }

  static getGroupsCanBeInvited(userId: string): Promise<AxiosResponse> {
    const url = `${urls.GET_GROUP_CAN_BE_INVITE}?userId=${userId}`;
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } }).then(res => res);
  }

  static sendGroupsChatInvite(roomJid: string, userId: string): Promise<AxiosResponse> {
    const url = urls.GROUP_CHAT_INVITE_MEMBER.replace('{roomJid}', encodeURIComponent(roomJid)).replace('{userId}', userId);
    return axios.post(url, null, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } }).then(res => res);
  }

  static getGroupChatMember(roomJid: string, userId: string): Promise<AxiosResponse> {
    const url = `${urls.GET_GROUP_CHAT_MEMBER}`.replace('{roomJid}', encodeURIComponent(roomJid)).replace('{userId}', userId);
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } }).then(res => res);
  }

  static getGroupChatDetail(roomJid: string): Promise<AxiosResponse> {
    const url = urls.GET_GROUP_CHAT_DETAIL.replace('{roomJid}', encodeURIComponent(roomJid));
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } }).then(res => res);
  }

  static getCurrentGroupChatMemberStatus(roomJid: string): Promise<AxiosResponse> {
    const url = `${urls.GET_CURRENT_GROUP_CHAT_MEMBER_STATUS}`.replace('{roomJid}', encodeURIComponent(roomJid));
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } }).then(res => res);
  }

  static getGroupChatMessageStatus(): Promise<AxiosResponse> {
    const url = urls.GET_GROUP_CHAT_USER_MESSAGE_STATUS;
    return axios.get(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } }).then(res => res);
  }

  static revokeGroupToken(roomJid: string): Promise<AxiosResponse> {
    const url = urls.REMOVE_GROUP_CHAT_TOKEN.replace('{roomJid}', encodeURIComponent(roomJid));
    return axios.post(url, null, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } }).then(res => res);
  }

  static setChatRoomReminder(roomId: number): Promise<AxiosResponse> {
    const url = urls.SET_CHAT_ROOM_REMINDER.replace('{RoomID}', roomId.toString());
    return axios.post(url, null, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } }).then(res => res);
  }

  static deleteChatRoomReminder(roomId: number): Promise<AxiosResponse> {
    const url = urls.DELETE_CHAT_ROOM_REMINDER.replace('{RoomID}', roomId.toString());
    return axios.delete(url, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } }).then(res => res);
  }
}

export default GroupChatApi;
